import type { Preset } from '../api/types.js';
import {
  DWELL_MS,
  type TourEvent,
  type TourSnapshot,
  type TourState,
  TRANSITION_MS,
} from './types.js';

// Pure reducer. No I/O, no Date.now — `now` always comes from the event so
// tests stay deterministic and the orchestrator controls the clock.
export function tourTransition(state: TourState, event: TourEvent, presetsLen: number): TourState {
  switch (event.type) {
    case 'START': {
      if (presetsLen === 0) return state;
      if (state.kind !== 'idle') return state;
      return { kind: 'transitioning', from: null, to: 0, startedAt: event.now, visited: [] };
    }
    case 'TICK': {
      if (state.kind === 'transitioning') {
        const elapsed = event.now - state.startedAt;
        if (elapsed < TRANSITION_MS) return state;
        return {
          kind: 'dwelling',
          index: state.to,
          startedAt: event.now,
          visited: dedup([...state.visited, state.to]),
        };
      }
      if (state.kind === 'dwelling') {
        const elapsed = event.now - state.startedAt;
        if (elapsed < DWELL_MS) return state;
        const next = state.index + 1;
        if (next >= presetsLen) return { kind: 'idle' };
        return {
          kind: 'transitioning',
          from: state.index,
          to: next,
          startedAt: event.now,
          visited: state.visited,
        };
      }
      return state;
    }
    case 'PAUSE': {
      if (state.kind === 'transitioning') {
        const elapsed = event.now - state.startedAt;
        return {
          kind: 'paused',
          pausedFromKind: 'transitioning',
          index: state.to,
          from: state.from,
          remainingMs: Math.max(0, TRANSITION_MS - elapsed),
          visited: state.visited,
        };
      }
      if (state.kind === 'dwelling') {
        const elapsed = event.now - state.startedAt;
        return {
          kind: 'paused',
          pausedFromKind: 'dwelling',
          index: state.index,
          from: null,
          remainingMs: Math.max(0, DWELL_MS - elapsed),
          visited: state.visited,
        };
      }
      return state;
    }
    case 'RESUME': {
      if (state.kind !== 'paused') return state;
      if (state.pausedFromKind === 'transitioning') {
        return {
          kind: 'transitioning',
          from: state.from,
          to: state.index,
          startedAt: event.now - (TRANSITION_MS - state.remainingMs),
          visited: state.visited,
        };
      }
      return {
        kind: 'dwelling',
        index: state.index,
        startedAt: event.now - (DWELL_MS - state.remainingMs),
        visited: state.visited,
      };
    }
    case 'GOTO': {
      if (event.index < 0 || event.index >= presetsLen) return state;
      const fromIdx =
        state.kind === 'dwelling'
          ? state.index
          : state.kind === 'transitioning'
            ? state.to
            : state.kind === 'paused'
              ? state.index
              : null;
      return {
        kind: 'transitioning',
        from: fromIdx,
        to: event.index,
        startedAt: event.now,
        visited: visitedFromState(state),
      };
    }
    case 'EXIT':
      return { kind: 'idle' };
  }
}

export function tourSnapshot(
  state: TourState,
  presets: readonly Preset[],
  now: number,
): TourSnapshot {
  if (state.kind === 'idle') {
    return {
      kind: 'idle',
      index: -1,
      total: presets.length,
      label: '',
      remainingMs: 0,
      visited: [],
    };
  }
  if (state.kind === 'transitioning') {
    return {
      kind: 'transitioning',
      index: state.to,
      total: presets.length,
      label: presets[state.to]?.label ?? '',
      remainingMs: 0,
      visited: state.visited,
    };
  }
  if (state.kind === 'dwelling') {
    const elapsed = now - state.startedAt;
    return {
      kind: 'dwelling',
      index: state.index,
      total: presets.length,
      label: presets[state.index]?.label ?? '',
      remainingMs: Math.max(0, DWELL_MS - elapsed),
      visited: state.visited,
    };
  }
  return {
    kind: 'paused',
    index: state.index,
    total: presets.length,
    label: presets[state.index]?.label ?? '',
    remainingMs: state.remainingMs,
    visited: state.visited,
  };
}

// Sort presets by order_index ascending, then by id for a stable tiebreak.
// Backend doesn't guarantee server-side ordering — sort client-side once at
// scene-loaded so all callers (orchestrator, pill, tests) agree on indexing.
export function sortPresets(presets: readonly Preset[]): readonly Preset[] {
  return [...presets].sort((a, b) => {
    if (a.order_index !== b.order_index) return a.order_index - b.order_index;
    return a.id.localeCompare(b.id);
  });
}

function visitedFromState(s: TourState): readonly number[] {
  if (s.kind === 'idle') return [];
  return s.visited;
}

function dedup(arr: readonly number[]): readonly number[] {
  return Array.from(new Set(arr));
}
